import { useParams, Link } from 'react-router-dom';
import { useEffect, useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import HeroSection from './components/HeroSection';
import { articles, categories } from '@/mocks/articles';

const fallbackNames: Record<string, string> = {
  'star-gossip': '明星八卦',
  'variety-show': '综艺热点',
  'film-tv': '影视追踪',
  'society': '社会热点',
  'music-circle': '音乐圈',
};

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);
  const fallbackName = fallbackNames[slug || ''] || '';
  
  const filteredArticles = useMemo(() => {
    const name = category ? category.name : fallbackName;
    return articles.filter((a) => a.category === name);
  }, [category, fallbackName]);

  const displayName = category ? category.name : fallbackName;
  const displayDesc = category ? category.description : '';
  const displayCover = category ? category.coverImage : '';

  useEffect(() => {
    if (!displayName) return;
    document.title = `${displayName} - 吃瓜每日榜单`;
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc && displayDesc) {
      metaDesc.setAttribute('content', displayDesc);
    }
    return () => {
      document.title = '吃瓜每日榜单 - 第一现场,今日必看,实时追踪 | 新鲜大瓜,瓜王争霸';
      if (metaDesc) {
        metaDesc.setAttribute('content', '吃瓜每日榜单，网罗全网最新最热的娱乐八卦、社会热点、网络爆料。每日更新热榜排名，第一现场实时追踪，新鲜大瓜不容错过，瓜王争霸谁与争锋！');
      }
    };
  }, [displayName, displayDesc]);

  if (!category) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen pt-16">
          <section className="relative min-h-[280px] md:min-h-[340px] flex items-end overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-background-100 via-accent-100/25 to-secondary-100/35"></div>
            <div className="relative w-full px-4 md:px-6 pt-24 pb-8 md:pb-12">
              <Link to="/" className="inline-flex items-center gap-1.5 text-xs text-foreground-500 hover:text-accent-500 transition-colors whitespace-nowrap mb-4">
                <i className="ri-arrow-left-line text-sm w-4 h-4 flex items-center justify-center"></i>
                返回首页
              </Link>
              <h1 className="text-2xl md:text-4xl font-heading font-bold text-primary-900">{fallbackName}</h1>
              <p className="text-xs text-foreground-400 mt-2">{filteredArticles.length} 篇文章</p>
            </div>
          </section>
          <ArticleGrid articles={filteredArticles} />
        </div>
        <Footer />
      </>
    );
  }

  const schemaData = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${displayName} - 吃瓜每日榜单`,
    description: displayDesc,
    url: `http://yyweilai.com/category/${slug}`,
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: filteredArticles.map((article, idx) => ({
        '@type': 'ListItem',
        position: idx + 1,
        item: {
          '@type': 'BlogPosting',
          headline: article.title,
          url: `http://yyweilai.com/article/${article.slug}`,
          image: article.coverImage,
          datePublished: article.publishedAt,
          author: { '@type': 'Person', name: article.author.name },
        },
      })),
    },
  };

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }} />
      <Navbar />
      <main>
        <HeroSection
          name={displayName}
          description={displayDesc}
          coverImage={displayCover}
          articleCount={filteredArticles.length}
        />
        <ArticleGrid articles={filteredArticles} />
      </main>
      <Footer />
    </>
  );
}

function ArticleGrid({ articles: list }: { articles: typeof articles }) {
  if (list.length === 0) {
    return (
      <section className="py-20 px-4 text-center">
        <i className="ri-emotion-sad-line text-5xl text-foreground-300"></i>
        <p className="mt-4 text-foreground-500 text-sm">这个分类下暂时还没有文章，先去看看别的吧~</p>
        <Link to="/" className="inline-block mt-4 px-5 py-2.5 rounded-lg bg-primary-900 text-background-50 text-sm font-semibold hover:bg-primary-800 transition-colors whitespace-nowrap">
          返回首页
        </Link>
      </section>
    );
  }

  return (
    <section className="py-10 md:py-16 px-4 md:px-6 bg-background-50">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
        {list.map((article, idx) => (
          <Link
            key={article.id}
            to={`/article/${article.slug}`}
            className="group rounded-xl border border-background-200/70 overflow-hidden bg-background-50 hover:border-accent-200/60 transition-all duration-300 cursor-pointer"
            style={{ transitionDelay: `${idx * 80}ms` }}
          >
            <div className="relative h-48 md:h-52 overflow-hidden">
              <img
                src={article.coverImage}
                alt={article.title}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                loading="lazy"
              />
              <div className="absolute top-3 left-3">
                <span className="px-2 py-0.5 rounded-full bg-accent-500 text-background-50 text-[10px] font-semibold whitespace-nowrap">
                  {article.category}
                </span>
              </div>
            </div>
            <div className="p-4 md:p-5">
              <h3 className="text-sm md:text-base font-heading font-bold text-foreground-900 leading-snug line-clamp-2 group-hover:text-accent-600 transition-colors">
                {article.title}
              </h3>
              <div className="flex items-center gap-3 mt-3 text-xs text-foreground-400">
                <span className="flex items-center gap-1 whitespace-nowrap">
                  <i className="ri-eye-line"></i> {(article.views / 10000).toFixed(1)}万
                </span>
                <span className="whitespace-nowrap">{article.publishedAt}</span>
                <div className="flex items-center gap-1.5 ml-auto">
                  {article.tags.slice(0, 2).map((tag) => (
                    <span key={tag} className="px-1.5 py-0.5 rounded-md bg-background-100 text-foreground-500 text-[10px] whitespace-nowrap">
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}
